import React from 'react';
import { Box, Card, Typography, SvgIconProps, styled } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

export interface StatCardProps {
  /** Título descriptivo de la estadística */
  title: string;
  /** Valor numérico o texto a mostrar con énfasis */
  value: string | number;
  /** Componente de icono de Material-UI */
  icon?: React.ComponentType<SvgIconProps>;
  /** Indicador de tendencia: positiva '+' o negativa '-' */
  trend?: '+' | '-';
  /** Texto adicional para la tendencia */
  trendText?: string;
}

// Componente estilizado para el contenedor del icono con gradiente
const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(2),
}));

// Componente estilizado para el indicador de tendencia
const TrendIndicator = styled(Box)<{ trend?: '+' | '-' }>(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  color: trend === '+' ? theme.palette.success.main : trend === '-' ? theme.palette.error.main : 'inherit',
  fontSize: '0.875rem',
  fontWeight: 500,
  marginTop: theme.spacing(1),
}));

/**
 * Componente StatCard para mostrar estadísticas con un diseño visual atractivo
 * 
 * Muestra un valor estadístico importante con título, icono y tendencia opcional.
 */
export const StatCard = ({
  title,
  value,
  icon: Icon,
  trend,
  trendText
}: StatCardProps) => {
  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {Icon && (
        <IconContainer>
          <Icon sx={{ fontSize: 28 }} />
        </IconContainer>
      )}
      
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      
      <Typography variant="h4" component="div" fontWeight={600} sx={{ mb: 1 }}>
        {value}
      </Typography>
      
      {trend && (
        <TrendIndicator trend={trend}>
          {trend === '+' ? (
            <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
          ) : (
            <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
          )}
          {trendText || (trend === '+' ? 'Incremento' : 'Decremento')}
        </TrendIndicator>
      )}
    </Card>
  );
};

export default StatCard;
